/**
 * MASTER 03 — VISUAL_STORY / «Несколько фотографий + текст».
 *
 * Editor UI + reader render for the visual-story spread. Reuses ImageSlot,
 * MediaLibraryPicker and CropModal from HeroMaster to keep tokens and
 * controls identical across the Editorial Engine.
 *
 * Design DNA:
 *   – editorial photo collage on the left (desktop) or stacked (mobile);
 *   – long-form text on the right (desktop) or below (mobile);
 *   – optional pull-quote and signature;
 *   – 2–5 photographs.
 */
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  ArrowUp, ArrowDown, Trash2, Plus, Crop, Image as ImageIcon,
} from "lucide-react";
import CropModal from "@/components/editorial/CropModal";
import {
  ImageSlot,
  MediaLibraryPicker,
  HERO_PALETTES,
  HERO_BACKGROUNDS,
} from "@/components/editorial/HeroMaster";

const MAX_IMAGES = 5;

// Migrate saved content from the old ad-hoc VISUAL_STORY shape (media_ids +
// body) to the new structured shape. Never mutates input; returns a normalised
// working copy used by the editor and the render.
function normaliseContent(raw) {
  const c = raw || {};
  const images = Array.isArray(c.images) && c.images.length
    ? c.images
    : (Array.isArray(c.media_ids) ? c.media_ids : []).map((mid) => ({
        media_id: mid, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1, alt: "",
      }));
  const text_blocks = Array.isArray(c.text_blocks) && c.text_blocks.length
    ? c.text_blocks
    : (c.body ? [{ id: "b1", body: c.body }] : []);
  return {
    eyebrow: c.eyebrow || "",
    headline: c.headline || "",
    subtitle: c.subtitle || "",
    text_blocks,
    quote: c.quote || "",
    signature: c.signature || "",
    caption: c.caption || "",
    images: images.slice(0, MAX_IMAGES),
  };
}

const genId = () => `b${Date.now().toString(36)}${Math.floor(Math.random() * 1e4)}`;

export function VisualStoryEditor({ block, onPatch }) {
  const c = useMemo(() => normaliseContent(block.content), [block.content]);
  const patch = (delta) => onPatch({ content: { ...c, ...delta } });

  // -------- image controls --------
  const [libraryFor, setLibraryFor] = useState(null); // index or null
  const [cropFor, setCropFor] = useState(null);       // index or null

  const setImageAt = (i, next) => {
    const arr = [...c.images];
    arr[i] = { ...arr[i], ...next };
    patch({ images: arr });
  };
  const addImage = () => {
    if (c.images.length >= MAX_IMAGES) return;
    patch({ images: [...c.images, { media_id: null, url: null, focal_x: 0.5, focal_y: 0.5, zoom: 1, alt: "" }] });
  };
  const removeImage = (i) => {
    const arr = [...c.images]; arr.splice(i, 1); patch({ images: arr });
  };
  const moveImage = (i, dir) => {
    const arr = [...c.images];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    patch({ images: arr });
  };
  const acceptFromLibrary = (m) => {
    if (libraryFor == null) return;
    setImageAt(libraryFor, { media_id: m.id, url: m.file_url || m.url });
    setLibraryFor(null);
  };
  const saveCrop = ({ focal_x, focal_y, zoom }) => {
    if (cropFor == null) return;
    setImageAt(cropFor, { focal_x, focal_y, zoom });
    setCropFor(null);
  };

  // -------- text-block controls --------
  const setBlockAt = (i, body) => {
    const arr = [...c.text_blocks];
    arr[i] = { ...arr[i], body };
    patch({ text_blocks: arr });
  };
  const addBlock = () => patch({
    text_blocks: [...c.text_blocks, { id: genId(), body: "" }],
  });
  const removeBlock = (i) => {
    const arr = [...c.text_blocks]; arr.splice(i, 1); patch({ text_blocks: arr });
  };
  const moveBlock = (i, dir) => {
    const arr = [...c.text_blocks];
    const j = i + dir; if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    patch({ text_blocks: arr });
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4" data-testid="visual-story-editor">
      {/* LEFT — text authoring */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-4">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Текст</div>

        <div>
          <label className="block text-[11px] opacity-60 mb-1">Надзаголовок</label>
          <Input value={c.eyebrow} onChange={(e) => patch({ eyebrow: e.target.value })}
                 placeholder="БИСКВИТЫ · 03" data-testid="vs-field-eyebrow" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Заголовок</label>
          <Input value={c.headline} onChange={(e) => patch({ headline: e.target.value })}
                 className="font-serif text-lg" data-testid="vs-field-headline" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Подзаголовок · необязательно</label>
          <Input value={c.subtitle} onChange={(e) => patch({ subtitle: e.target.value })}
                 data-testid="vs-field-subtitle" />
        </div>

        <div className="pt-2">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] opacity-60">Текстовые блоки</div>
            <button type="button" onClick={addBlock}
              className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100"
              data-testid="vs-add-text-block">
              <Plus className="w-3.5 h-3.5" /> Добавить блок
            </button>
          </div>
          <div className="space-y-2">
            {c.text_blocks.length === 0 && (
              <div className="text-[12px] opacity-50">Пока нет ни одного блока текста.</div>
            )}
            {c.text_blocks.map((tb, i) => (
              <div key={tb.id || i} className="rounded-lg border hairline bg-cream/40 p-2"
                   data-testid={`vs-text-block-${i}`}>
                <div className="flex items-center justify-end gap-1 mb-1">
                  <button type="button" onClick={() => moveBlock(i, -1)} disabled={i === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    data-testid={`vs-text-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => moveBlock(i, +1)} disabled={i === c.text_blocks.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    data-testid={`vs-text-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => removeBlock(i)}
                    className="p-1 opacity-60 hover:opacity-100 text-red-600"
                    data-testid={`vs-text-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <textarea rows={4} value={tb.body || ""}
                  onChange={(e) => setBlockAt(i, e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm font-serif leading-relaxed"
                  data-testid={`vs-text-body-${i}`} />
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[11px] opacity-60 mb-1">Акцентная цитата · необязательно</label>
          <Input value={c.quote} onChange={(e) => patch({ quote: e.target.value })}
                 data-testid="vs-field-quote" />
        </div>
        <div>
          <label className="block text-[11px] opacity-60 mb-1">Подпись · необязательно</label>
          <Input value={c.signature} onChange={(e) => patch({ signature: e.target.value })}
                 placeholder="— Оксана Анокина" data-testid="vs-field-signature" />
        </div>
      </div>

      {/* RIGHT — image collage authoring */}
      <div className="rounded-2xl bg-white border hairline p-4 space-y-3">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Фотографии · до {MAX_IMAGES}</div>
          <button type="button" onClick={addImage} disabled={c.images.length >= MAX_IMAGES}
            className="text-[12px] inline-flex items-center gap-1 opacity-80 hover:opacity-100 disabled:opacity-30"
            data-testid="vs-add-image">
            <Plus className="w-3.5 h-3.5" /> Добавить фото
          </button>
        </div>

        {c.images.length === 0 && (
          <div className="rounded-lg bg-cream/60 border border-dashed border-espresso/25 p-4 text-center">
            <ImageIcon className="w-6 h-6 mx-auto opacity-50 mb-1" />
            <div className="text-[12px] opacity-70 mb-2">Добавьте до {MAX_IMAGES} фотографий.</div>
            <button type="button" onClick={addImage}
              className="text-[12px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-espresso text-cream"
              data-testid="vs-empty-add-image">
              <Plus className="w-3.5 h-3.5" /> Добавить первое фото
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {c.images.map((img, i) => (
            <div key={i} className="space-y-2" data-testid={`vs-image-${i}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest opacity-60">Фото {i + 1}</div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    data-testid={`vs-image-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => moveImage(i, +1)} disabled={i === c.images.length - 1}
                    className="p-1 opacity-60 hover:opacity-100 disabled:opacity-25"
                    data-testid={`vs-image-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <ImageSlot
                label=""
                url={img.url}
                focalX={img.focal_x ?? 0.5} focalY={img.focal_y ?? 0.5} zoom={img.zoom || 1}
                onFile={(m) => setImageAt(i, { media_id: m.id, url: m.file_url })}
                onClear={() => removeImage(i)}
                onOpenLibrary={() => setLibraryFor(i)}
                onOpenCrop={() => setCropFor(i)}
                testid={`vs-image-slot-${i}`} />
              <Input placeholder="Alt-текст · необязательно"
                value={img.alt || ""}
                onChange={(e) => setImageAt(i, { alt: e.target.value })}
                data-testid={`vs-image-alt-${i}`} />
            </div>
          ))}
        </div>
      </div>

      <MediaLibraryPicker
        open={libraryFor != null}
        onClose={() => setLibraryFor(null)}
        onPick={acceptFromLibrary} />
      <CropModal
        open={cropFor != null}
        imageUrl={cropFor != null ? c.images[cropFor]?.url : null}
        initialFocalX={cropFor != null ? c.images[cropFor]?.focal_x ?? 0.5 : 0.5}
        initialFocalY={cropFor != null ? c.images[cropFor]?.focal_y ?? 0.5 : 0.5}
        initialZoom={cropFor != null ? c.images[cropFor]?.zoom || 1 : 1}
        onCancel={() => setCropFor(null)}
        onSave={saveCrop} />
    </div>
  );
}

// -------------------- READER RENDER --------------------

function CollageImage({ img, className = "" }) {
  if (!img?.url) {
    return (
      <div className={`bg-espresso/5 border border-dashed border-espresso/20 ${className}`} />
    );
  }
  return (
    <div className={`overflow-hidden bg-neutral-100 ${className}`}>
      <img
        src={img.url}
        alt={img.alt || ""}
        className="w-full h-full object-cover"
        style={{
          transform: `scale(${img.zoom || 1})`,
          transformOrigin: `${(img.focal_x ?? 0.5) * 100}% ${(img.focal_y ?? 0.5) * 100}%`,
        }}
      />
    </div>
  );
}

// Editorial mosaic — asymmetric, varied spans, no card look. All heights are
// controlled through a base row so images never lose the editorial rhythm.
function CollageDesktop({ images }) {
  const n = images.length;
  if (n === 0) return null;
  if (n === 1) {
    return <CollageImage img={images[0]} className="w-full aspect-[4/5]" />;
  }
  if (n === 2) {
    // Asymmetric — tall lead + shorter portrait offset downward.
    return (
      <div className="grid grid-cols-12 gap-2">
        <CollageImage img={images[0]} className="col-span-7 row-span-2 aspect-[3/4]" />
        <div className="col-span-5 pt-10">
          <CollageImage img={images[1]} className="aspect-[4/5]" />
        </div>
      </div>
    );
  }
  if (n === 3) {
    // Packed rectangle: lead spans two rows on the left; two companions stack
    // on the right. Container aspect ratio fixes overall height so both rows
    // match the lead's height exactly — no empty cells.
    return (
      <div className="grid grid-cols-12 grid-rows-2 gap-2 aspect-[5/6]">
        <CollageImage img={images[0]} className="col-span-7 row-span-2" />
        <CollageImage img={images[1]} className="col-span-5 row-span-1" />
        <CollageImage img={images[2]} className="col-span-5 row-span-1" />
      </div>
    );
  }
  if (n === 4) {
    // Packed rectangle: lead spans two rows; row 1 right = wide companion,
    // row 2 right = two smaller companions (3+2 = 5). Total = 12 per row.
    return (
      <div className="grid grid-cols-12 grid-rows-2 gap-2 aspect-[5/6]">
        <CollageImage img={images[0]} className="col-span-7 row-span-2" />
        <CollageImage img={images[1]} className="col-span-5 row-span-1" />
        <CollageImage img={images[2]} className="col-span-3 row-span-1" />
        <CollageImage img={images[3]} className="col-span-2 row-span-1" />
      </div>
    );
  }
  // n === 5 — packed 3-row editorial mosaic. Every row sums to 12 columns and
  // occupies exactly one row of the grid, so the whole composition forms a
  // perfect rectangle with only gap-2 seams.
  //   Row 1: left large (8) + right portrait (4)
  //   Row 2: left small (5) + right wider (7)
  //   Row 3: full-width strip (12)
  return (
    <div className="grid grid-cols-12 grid-rows-3 gap-2 aspect-[3/4]">
      <CollageImage img={images[0]} className="col-span-8 row-span-1" />
      <CollageImage img={images[1]} className="col-span-4 row-span-1" />
      <CollageImage img={images[2]} className="col-span-5 row-span-1" />
      <CollageImage img={images[3]} className="col-span-7 row-span-1" />
      <CollageImage img={images[4]} className="col-span-12 row-span-1" />
    </div>
  );
}

function CollageMobile({ images }) {
  const n = images.length;
  if (n === 0) return null;
  if (n === 1) {
    return <CollageImage img={images[0]} className="w-full aspect-[4/5]" />;
  }
  // 2..5 — compact two-column editorial mosaic; first image may span both cols.
  const lead = images[0];
  const rest = images.slice(1);
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="visual-story-mobile-collage">
      <CollageImage img={lead} className="col-span-2 aspect-[4/5]" />
      {rest.map((img, i) => {
        // Break the grid rhythm slightly on odd counts so it does not look
        // like four identical squares.
        const isTall = rest.length === 3 && i === 0;
        return (
          <CollageImage
            key={i}
            img={img}
            className={isTall ? "row-span-2 aspect-[4/6]" : "aspect-[5/4]"}
          />
        );
      })}
    </div>
  );
}

export function VisualStoryRender({ block, mode = "desktop" }) {
  const c = useMemo(() => normaliseContent(block.content), [block.content]);
  const isMobile = mode === "mobile";

  const bg = HERO_BACKGROUNDS.find((b) => b.key === (block.settings?.background)) || HERO_BACKGROUNDS[0];
  const palette = HERO_PALETTES.find((p) => p.key === (block.settings?.palette)) || HERO_PALETTES[0];

  const Text = (
    <div className="max-w-[62ch]">
      {c.eyebrow && (
        <div className="text-[10px] tracking-[0.28em] uppercase mb-3" style={{ color: palette.accent }}>
          {c.eyebrow}
        </div>
      )}
      {c.headline && (
        <h2 className="font-serif leading-[1.1] mb-3"
            style={{ fontSize: isMobile ? "clamp(26px, 6vw, 32px)" : "clamp(30px, 3.4vw, 44px)",
                     color: "#2A1D14" }}>
          {c.headline}
        </h2>
      )}
      {c.subtitle && (
        <div className="text-[15px] leading-[1.55] mb-6" style={{ color: "#5B4326" }}>
          {c.subtitle}
        </div>
      )}
      <div className="space-y-4">
        {c.text_blocks.map((tb, i) => (
          <p key={tb.id || i}
             className="font-serif text-[16px] leading-[1.75]"
             style={{ color: "#3E2E20" }}>
            {tb.body}
          </p>
        ))}
      </div>
      {c.quote && (
        <blockquote className="mt-6 pl-4 border-l-2 font-serif italic text-[18px] leading-[1.5]"
                    style={{ borderColor: palette.accent, color: "#3E2E20" }}>
          «{c.quote}»
        </blockquote>
      )}
      {c.signature && (
        <div className="mt-6 text-[13px]" style={{ color: palette.accent }}>
          {c.signature}
        </div>
      )}
      {c.caption && (
        <div className="mt-4 text-[12px] opacity-70" style={{ color: "#5B4326" }}>
          {c.caption}
        </div>
      )}
    </div>
  );

  return (
    <section
      className="w-full overflow-hidden"
      style={{ background: bg.css || "#EFE6D5" }}
      data-testid={`visual-story-render-${block.id}-${mode}`}
    >
      <div className={`mx-auto px-6 sm:px-10 py-10 sm:py-14 ${isMobile ? "" : "max-w-[1200px]"}`}>
        {isMobile ? (
          <>
            <div className="mb-6">
              {c.eyebrow && (
                <div className="text-[10px] tracking-[0.28em] uppercase mb-2" style={{ color: palette.accent }}>
                  {c.eyebrow}
                </div>
              )}
              {c.headline && (
                <h2 className="font-serif leading-[1.1] mb-2"
                    style={{ fontSize: "clamp(26px, 6vw, 32px)", color: "#2A1D14" }}>
                  {c.headline}
                </h2>
              )}
              {c.subtitle && (
                <div className="text-[15px] leading-[1.55]" style={{ color: "#5B4326" }}>
                  {c.subtitle}
                </div>
              )}
            </div>
            <div className="mb-6"><CollageMobile images={c.images} /></div>
            {/* Text/quote/signature reuse the same block without eyebrow/headline (already shown) */}
            <div className="max-w-[62ch]">
              <div className="space-y-4">
                {c.text_blocks.map((tb, i) => (
                  <p key={tb.id || i} className="font-serif text-[16px] leading-[1.75]" style={{ color: "#3E2E20" }}>
                    {tb.body}
                  </p>
                ))}
              </div>
              {c.quote && (
                <blockquote className="mt-6 pl-4 border-l-2 font-serif italic text-[18px] leading-[1.5]"
                            style={{ borderColor: palette.accent, color: "#3E2E20" }}>
                  «{c.quote}»
                </blockquote>
              )}
              {c.signature && (
                <div className="mt-6 text-[13px]" style={{ color: palette.accent }}>
                  {c.signature}
                </div>
              )}
              {c.caption && (
                <div className="mt-4 text-[12px] opacity-70" style={{ color: "#5B4326" }}>
                  {c.caption}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-10 lg:gap-14 items-start">
            <div><CollageDesktop images={c.images} /></div>
            <div>{Text}</div>
          </div>
        )}
      </div>
    </section>
  );
}
